import { useCallback, useEffect, useRef, useState } from 'react';

import OrganizationManager from '@gasbi/shared/common/utils/organizationManager';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { agentsApi } from '@/features/agents/agentsApi';
import { useSession } from '@/features/session/SessionProvider';
import {
  type ChatHostContext,
  type ChatToHostMessage,
  isHostToChatMessage,
} from '@/launcher/protocol';
import http from '@/utils/http';

/** A framed panel waits this long for `init`; then it goes on without the host app's context. */
export const INIT_TIMEOUT_MS = 3_000;

// A menu, a select or a dialog of the panel closes itself on Escape; only a bare Escape closes the panel. An open
// menu or select is told by its trigger: Joy keeps a closed select's listbox mounted, hidden.
const OPEN_POPUP_SELECTOR =
  '[aria-haspopup][aria-expanded="true"], [role="dialog"]';

const isFramed = () => window.parent !== window;

/**
 * The host app's organization is the panel's one: it goes to the state, so every request carries it as the
 * `organizationId` header (ADR-0004 §2.7) — the server verifies the membership. On top, the chat's own cookie session
 * remembers it (GSB-8274, PO 2026-10-25): the host's choice is explicit, so the session takes it with a POST and
 * `onSwitched` refetches the lists — no reload of the panel. Until the session endpoint exists (GSB-8279: 404) and
 * before it has answered, the state alone, as before.
 */
const followHostOrganization = (hostOrganizationId: string | undefined) => {
  OrganizationManager.setState(hostOrganizationId ?? null);
  if (!hostOrganizationId || !OrganizationManager.isSessionBacked()) return;
  if (OrganizationManager.getActiveOrganizationId() === hostOrganizationId)
    return;
  // A failed switch (no access to the host's organization here) leaves the session's organization.
  OrganizationManager.switchTo(hostOrganizationId).catch(() => undefined);
};

/**
 * The panel's side of the launcher protocol (ADR-0004 §2.7, `src/launcher/protocol.ts`). Once mounted it posts
 * `ready` to the parent; the launcher answers with `init` and follows with `context` on every change. Only the
 * parent's messages count; the origin of the first `init` is remembered, later messages come only from it, and
 * `close`/`unread` go only to it. The organization goes to the shared `OrganizationManager` and to the chat's own
 * session (`followHostOrganization`; the axios interceptor and the stream headers read the manager), the host app's
 * user to `ensureUser` (§2.5).
 *
 * `ready` is false until `init` arrives, so the panel never shows a chat before it knows whose it should be; a page
 * opened outside a frame, or a frame without `init` after `INIT_TIMEOUT_MS`, goes on with `?app=` alone.
 */
export const useEmbedHost = () => {
  const [searchParams] = useSearchParams();
  const { state, ensureUser } = useSession();
  const dispatch = useDispatch();
  const framed = isFramed();
  const [ready, setReady] = useState(!framed);
  // The app key of the iframe's address: the launcher names the same key in `init`.
  const appFromAddress = searchParams.get('app') ?? '';
  // `?app=` until `init` names the app
  const [app, setApp] = useState(appFromAddress);
  const hostOrigin = useRef<string | null>(null);
  // The latest organization the host named; applied again once the chat's session has answered.
  const hostOrganizationId = useRef<string | undefined>(undefined);
  const [isOrganizationSessionRead, setIsOrganizationSessionRead] =
    useState(false);
  const signedIn = state.status === 'signedIn';
  // The handshake runs once per load: the latest ensureUser is read through a ref instead of re-running it.
  const ensureUserRef = useRef(ensureUser);
  useEffect(() => {
    ensureUserRef.current = ensureUser;
  }, [ensureUser]);

  // The chat's own organization session (GSB-8274), read once signed in (before that the endpoint answers 401). The
  // session calls name the embedding app (`?app=`: the Agents backend narrows its membership check to that app's host,
  // GSB-8279). A switch resets the agents cache, so the lists refetch for the new organization instead of the panel
  // reloading.
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;

    OrganizationManager.initialize({
      http,
      app: appFromAddress,
      onSwitched: () => {
        dispatch(agentsApi.util.resetApiState());
      },
    })
      .catch(() => null)
      .then(() => {
        if (!cancelled) setIsOrganizationSessionRead(true);
      });

    return () => {
      cancelled = true;
    };
  }, [signedIn, appFromAddress, dispatch]);

  useEffect(() => {
    if (isOrganizationSessionRead)
      followHostOrganization(hostOrganizationId.current);
  }, [isOrganizationSessionRead]);

  useEffect(() => {
    if (!framed) return;

    const apply = ({ organizationId, userSub }: ChatHostContext) => {
      hostOrganizationId.current = organizationId;
      followHostOrganization(organizationId);
      // §2.5: without the host app's user there is nothing to compare.
      if (userSub) ensureUserRef.current(userSub);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || !isHostToChatMessage(event.data))
        return;
      const message = event.data;
      if (message.type === 'gasbi-ai:init' && hostOrigin.current === null)
        hostOrigin.current = event.origin;
      if (event.origin !== hostOrigin.current) return;

      // Applied before the state below renders: a session of another user starts switching in the same render.
      apply(message);
      if (message.type === 'gasbi-ai:init') {
        setApp(previous => message.app || previous);
        setReady(true);
      }
    };

    window.addEventListener('message', onMessage);
    // `ready` carries nothing, so any origin may receive it; everything after it goes to the origin of `init`.
    window.parent.postMessage({ type: 'gasbi-ai:ready' }, '*');
    const timer = window.setTimeout(() => setReady(true), INIT_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
    };
  }, [framed]);

  const post = useCallback((message: ChatToHostMessage) => {
    if (hostOrigin.current)
      window.parent.postMessage(message, hostOrigin.current);
  }, []);

  const close = useCallback(() => post({ type: 'gasbi-ai:close' }), [post]);

  const notifyUnread = useCallback(
    () => post({ type: 'gasbi-ai:unread', count: 1 }),
    [post],
  );

  // Keys pressed in the panel stay in the iframe's document, so the launcher never sees Escape: the panel closes
  // itself. Captured before any handler of the panel runs, while an open menu or select is still in the DOM.
  useEffect(() => {
    if (!framed) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector(OPEN_POPUP_SELECTOR)) return;
      close();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [framed, close]);

  return { framed, ready, app, close, notifyUnread };
};
